// src/components/ui/AfriSwitch.jsx
import React from "react";

/*
 * AfriSwitch — 2-option segmented control (Email | Phone)
 * - Pure/presentational (no hooks inside this component)
 * - Uses AfriMed tokens; ACTIVE uses secondary (teal) with white text
 * - Keyboard: Left/Right arrows, Home/End
 */
export default function AfriSwitch({
  value = "email",              // "email" | "phone"
  onChange,                     // (next) => void
  options = [
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
  ],
  ariaLabel = "Choose sign-in method",
  className = "",
  variant = "segmented",
  disabled = false,
}) {
  if (variant === "toggle") {
    const isOn = value === "on" || value === true;
    const trackStyle = {
      width: "52px",
      height: "32px",
      flex: "0 0 52px",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: isOn ? "flex-end" : "flex-start",
      border: 0,
      borderRadius: "16px",
      padding: "4px",
      background: isOn ? "rgb(var(--afrimed-secondary))" : "var(--state-on-surface-16)",
      boxShadow: "none",
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.56 : 1,
    };
    const thumbStyle = {
      width: "24px",
      height: "24px",
      flex: "0 0 24px",
      borderRadius: "50%",
      // This remains light in dark mode. Surface becomes dark there and caused
      // the switch thumb to disappear against its neutral track.
      background: "rgb(var(--afrimed-on-secondary))",
      boxShadow: "var(--elev-1)",
    };
    return (
      <button
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-label={ariaLabel}
        disabled={disabled}
        className={`afri-switch afri-switch--toggle ${isOn ? "is-on" : "is-off"} focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[rgb(var(--afrimed-primary))] ${className}`}
        style={trackStyle}
        onClick={() => !disabled && onChange?.(isOn ? "off" : "on")}
      >
        <span className="afri-switch__thumb" aria-hidden="true" style={thumbStyle} />
      </button>
    );
  }

  function handleKey(e) {
    const idx = options.findIndex((o) => o.key === value);
    if (idx < 0) return;

    if (e.key === "ArrowRight") {
      const next = options[(idx + 1) % options.length];
      onChange?.(next.key);
      e.preventDefault();
    } else if (e.key === "ArrowLeft") {
      const next = options[(idx - 1 + options.length) % options.length];
      onChange?.(next.key);
      e.preventDefault();
    } else if (e.key === "Home") {
      onChange?.(options[0].key);
      e.preventDefault();
    } else if (e.key === "End") {
      onChange?.(options[options.length - 1].key);
      e.preventDefault();
    }
  }

  return (
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={`inline-flex items-center rounded-full p-1 ${className}`}
        style={{
          background: "rgb(var(--clr-surface-cont-highest))",
          border: "0.5px solid rgba(var(--afrimed-control-outline), 0.88)",
          boxShadow: "none",
          backdropFilter: "none",
          WebkitBackdropFilter: "none",
        }}
        onKeyDown={handleKey}
      >
      {options.map((opt, i) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            type="button"
            onClick={() => onChange?.(opt.key)}
            className={`px-5 h-9 rounded-full text-sm font-medium transition outline-none ${
              active ? "shadow-sm" : "text-on-surface-var"
            } focus-visible:ring-2 focus-visible:ring-[rgb(var(--afrimed-primary))]`}
            style={
              active
                ? {
                    background: "rgb(var(--afrimed-secondary))",
                    color: "rgb(var(--afrimed-on-secondary))",
                  }
                : undefined
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
